/**
 * GameOver — final summary + rematch / back-to-lobby controls.
 *
 * v1.4.1 Phase 22.1: host sees primary "Play again, same seats" + secondary
 * "Shuffle partners" buttons (both fire `actions.rematch`); everyone sees
 * the tertiary "Back to lobby" button (`actions.continueRound`). Non-host
 * sees only the lobby button. Renders the volatile per-room series tally
 * chip ("Series 2-1") under the winner label when the cumulative count
 * is non-zero; volatile per owner decision #5 so the chip disappears on
 * server restart. The Phase 14 game Continue gate predicate
 * (`view.gateKind === "game"`) is unchanged.
 */
import type { RoundResult } from "@hookum/protocol";
import { Suspense, lazy } from "react";
import { actions } from "../wire/index.js";
import { useLobby, useView } from "../state/hooks.js";

// Phase 14 plan adjustment #4: confetti via `useEffect` dynamic import.
// We additionally wrap the component in React.lazy so the chunk only
// downloads when a winner is on screen (the 8 kB target). The
// `canvas-confetti` library itself is loaded inside the component's
// useEffect, gated by `prefers-reduced-motion`.
const Confetti = lazy(() =>
  import("./Confetti.js").then((m) => ({ default: m.Confetti })),
);

const SUIT_GLYPH: Record<"C" | "D" | "H" | "S", string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};
const SUIT_NAME: Record<"C" | "D" | "H" | "S", string> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

function suitClass(suit: "C" | "D" | "H" | "S"): string {
  return suit === "D" || suit === "H" ? "suit-red" : "suit-black";
}

function teamForSeat(seat: number): "A" | "B" {
  return seat % 2 === 0 ? "A" : "B";
}

interface Aggregate {
  roundsWonA: number;
  roundsWonB: number;
  pushes: number;
  diffA: number;
  diffB: number;
  totalA: number;
  totalB: number;
}

function aggregate(ledger: readonly RoundResult[]): Aggregate {
  let roundsWonA = 0;
  let roundsWonB = 0;
  let pushes = 0;
  let diffA = 0;
  let diffB = 0;
  let totalA = 0;
  let totalB = 0;
  if (!ledger) return { roundsWonA, roundsWonB, pushes, diffA, diffB, totalA, totalB };
  for (const r of ledger) {
    if (r.winner === "A") roundsWonA++;
    else if (r.winner === "B") roundsWonB++;
    else pushes++;
    diffA += r.delta.A ?? 0;
    diffB += r.delta.B ?? 0;
    totalA += r.tricks.A ?? 0;
    totalB += r.tricks.B ?? 0;
  }
  return { roundsWonA, roundsWonB, pushes, diffA, diffB, totalA, totalB };
}

export function GameOver(): JSX.Element | null {
  const view = useView();
  const lobby = useLobby();
  if (!view) return null;
  // v1.2 Phase 14: gate on `view.gateKind === "game"`. The server emits the
  // "game" gate whenever the engine emits `gameOver`; it clears only when
  // every connected human acks → `resetToLobby` (legacy "Continue" path)
  // OR when the host triggers a rematch (Phase 22.1 — clears the gate +
  // installs a fresh engine in one step).
  if (view.gateKind !== "game") return null;

  const agg = aggregate(view.ledger);
  const winner = view.gameWinner;
  const winnerLabel =
    winner === "DRAW" || winner === null
      ? "Draw"
      : `Team ${winner} wins`;

  const youTeam = teamForSeat(view.you);
  const outcome: "won" | "lost" | "draw" =
    winner === "DRAW" || winner === null
      ? "draw"
      : winner === youTeam
        ? "won"
        : "lost";
  const outcomeLabel =
    outcome === "won"
      ? "You won the game!"
      : outcome === "lost"
        ? "You lost."
        : "It's a draw.";

  // v1.4.1 Phase 22.1: host detection. lobby.hostId is the host's seat
  // index as a string; pre-game (no lobby yet) defaults to non-host so
  // the rematch buttons stay hidden.
  const isHost = lobby !== null && lobby.hostId === String(view.you);

  // v1.4.1 Phase 22.1: series tally chip. Volatile per-room (owner
  // decision #5). Winner-first ordering so the chip reads naturally
  // ("Series 2-1" from the winner's POV).
  const sA = view.seriesScore?.A ?? 0;
  const sB = view.seriesScore?.B ?? 0;
  const seriesChip =
    sA + sB === 0 ? null : winner === "B" ? `Series ${sB}-${sA}` : `Series ${sA}-${sB}`;

  const acks = view.roundAcks ?? [];
  const totalSeats = view.handCounts.length;
  const seatsNotAcked: number[] = [];
  for (let s = 0; s < totalSeats; s++) {
    if (!acks.includes(s)) seatsNotAcked.push(s);
  }
  const youAcked = view.youAcked ?? acks.includes(view.you);
  const waitingForCount = seatsNotAcked.length;
  const waitingHint =
    waitingForCount === 0
      ? "Returning to lobby…"
      : waitingForCount === 1
        ? "Waiting for 1 player…"
        : `Waiting for ${waitingForCount} players…`;

  const onBack = (): void => {
    if (!youAcked) actions.continueRound();
  };

  const fmtDelta = (d: number): string => (d > 0 ? `+${d}` : `${d}`);

  return (
    <div
      className="game-over-overlay"
      role="dialog"
      aria-labelledby="game-over-title"
      aria-modal="true"
    >
      {/* Lazy confetti: only renders for the winning team. Reduced-motion
       * users see the banner without the animation. */}
      {outcome === "won" ? (
        <Suspense fallback={null}>
          <Confetti />
        </Suspense>
      ) : null}
      <div className="game-over-card">
        <h2 id="game-over-title" className="game-over-title">
          {winnerLabel}
        </h2>
        {seriesChip ? (
          <p className="game-over-series">{seriesChip}</p>
        ) : null}
        <p
          className={`game-over-outcome game-over-outcome-${outcome}`}
          aria-live="polite"
        >
          {outcomeLabel}
        </p>
        <div className="game-over-aggregates">
          <div className="game-over-agg">
            <span className="game-over-agg-label">Rounds won</span>
            <span className="game-over-agg-value">
              <span className={winner === "A" ? "is-winner" : ""}>A: {agg.roundsWonA}</span>{" "}
              ·{" "}
              <span className={winner === "B" ? "is-winner" : ""}>B: {agg.roundsWonB}</span>
              {agg.pushes > 0 ? (
                <span className="game-over-pushes"> · pushes: {agg.pushes}</span>
              ) : null}
            </span>
          </div>
          <div className="game-over-agg">
            <span className="game-over-agg-label">Differential</span>
            <span className="game-over-agg-value">
              A: {fmtDelta(agg.diffA)} · B: {fmtDelta(agg.diffB)}
            </span>
          </div>
          <div className="game-over-agg">
            <span className="game-over-agg-label">Total tricks</span>
            <span className="game-over-agg-value">
              A: {agg.totalA} · B: {agg.totalB}
            </span>
          </div>
        </div>
        <div className="game-over-ledger-wrap">
          <table className="game-over-ledger" aria-label="Round-by-round ledger">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Bid</th>
                <th scope="col">Trump</th>
                <th scope="col">Contract</th>
                <th scope="col">A</th>
                <th scope="col">B</th>
                <th scope="col">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {view.ledger.map((r) => (
                <tr key={r.round}>
                  <td>{r.round + 1}</td>
                  <td>{r.bid}</td>
                  <td>
                    <span
                      className={`hud-suit ${suitClass(r.trump)}`}
                      aria-label={SUIT_NAME[r.trump]}
                    >
                      {SUIT_GLYPH[r.trump]}
                    </span>
                  </td>
                  <td>Team {r.contractTeam}</td>
                  <td>
                    {r.tricks.A ?? 0}{" "}
                    <span className="game-over-delta">
                      ({fmtDelta(r.delta.A ?? 0)})
                    </span>
                  </td>
                  <td>
                    {r.tricks.B ?? 0}{" "}
                    <span className="game-over-delta">
                      ({fmtDelta(r.delta.B ?? 0)})
                    </span>
                  </td>
                  <td>
                    {r.winner ? `Team ${r.winner}` : "Push"}
                    {r.forfeited ? (
                      <span className="game-over-forfeit-tag" title="Joker-lead forfeit">
                        {" "}
                        (forfeit)
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="game-over-actions">
          {isHost && (
            <>
              <button
                type="button"
                onClick={() => actions.rematch("same_seats")}
                className="game-over-rematch-primary"
              >
                Play again, same seats
              </button>
              <button
                type="button"
                onClick={() => actions.rematch("shuffle_partners")}
                className="game-over-rematch-secondary"
              >
                Shuffle partners
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onBack}
            className="game-over-continue game-over-continue-tertiary"
            disabled={youAcked}
            aria-label={youAcked ? "Waiting for other players" : "Back to lobby"}
          >
            {youAcked ? "Waiting…" : "Back to lobby"}
          </button>
          <p className="game-over-waiting" aria-live="polite">
            {waitingHint}
          </p>
        </div>
      </div>
    </div>
  );
}
